import React, { useCallback, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { Map, MapPointProps } from "../framework/map/map";
import LocalShippingIcon from "@mui/icons-material/LocalShippingOutlined";
import {
    getWorkTimeTypeColorClass,
    getWorkTimeTypeColorCode,
    getWorkTimeTypeContrastColorClass,
} from "../../models/workShitTimeSlot/workTimeType";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import {
    setSelectedWorkShiftTimeSlotId,
    setHoveredWorkShiftTimeSlotId,
} from "../../store/monitorViewSlice";
import {
    fetchGeofences,
} from "../../store/geofenceSlice";
import { GeofenceDrawing } from "./geofence/geofenceDrawing";
import { GeofenceOverlay } from "./geofence/geofenceOverlay";
import { GeofenceLegend } from "./geofence/geofenceLegend";
import { MapControl, ControlPosition } from "@vis.gl/react-google-maps";
import { useWorkShiftTimeSlotMapData } from "../../hooks/workTime/useWorkShiftTimeSlotMapData";
import { useVehicleRouteMapData } from "../../hooks/monitorView/useVehicleRouteMapData";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { VehicleData } from "../../models/monitorView/monitorViewTypes";
import { handleVehicleSelect } from "./monitorViewUtils";
import { Base } from "../../framework/base";
import { MapToolbar } from "./mapToolbar";
import { useMapZoom } from "../../hooks/monitorView/useMapZoom";

interface MonitorViewMapProps {
    loading: boolean;
    data: VehicleData[];
    onRefresh: () => void;
    reloadKey: number;
}

export const MonitorViewMap = React.memo(({
    loading,
    data,
    onRefresh,
    reloadKey,
}: MonitorViewMapProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const groupPoints = useAppSelector(
        (state) => state.monitorView.groupMapPoints
    );
    const selectedWorkShiftTimeSlotId = useAppSelector(
        (state) => state.monitorView.selectedWorkShiftTimeSlotId
    );
    const selectedVehicleId = useAppSelector(
        (state) => state.monitorView.selectedVehicleId
    );
    const workShift = useAppSelector(
        (state) => state.monitorView.selectedWorkShift
    );
    const hoveredWorkShiftTimeSlotId = useAppSelector(
        (state) => state.monitorView.hoveredWorkShiftTimeSlotId
    );
    const isWorkShiftView = useAppSelector(
        (state) => state.monitorView.isWorkShiftView
    );
    const isVehicleRouteView = useAppSelector(
        (state) => state.monitorView.isVehicleRouteView
    );
    const isVehicleDetailView = useAppSelector(
        (state) => state.monitorView.isVehicleDetailView
    );
    const isVehicleShiftTabView = useAppSelector(
        (state) => state.monitorView.isVehicleShiftTabView
    );
    const vehicleRoute = useAppSelector(
        (state) => state.monitorView.vehicleRoute
    );
    const selectedVehicleShift = useAppSelector(
        (state) => state.monitorView.selectedVehicleShift
    );
    const vehicleRouteLoading = useAppSelector(
        (state) => state.monitorView.loading
    );
    const showNoWorkTime = useAppSelector(
        (state) => state.monitorViewFilters.noWorkTime
    );
    const selectedWorkTimeTypeIds = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypeIds
    );
    const drawingMode = useAppSelector((state) => state.geofence.drawingMode);
    const showOnMap = useAppSelector((state) => state.geofence.showOnMap);
    const editMode = useAppSelector((state) => state.geofence.editMode);

    const {
        routes: workshiftTimeSlotRoutes,
        points: workshiftPoints,
        reload,
        loading: mapDataLoading,
    } = useWorkShiftTimeSlotMapData(
        workShift?.workShiftTimeSlots ?? [],
        selectedWorkShiftTimeSlotId,
        hoveredWorkShiftTimeSlotId
    );

    const { routes: vehicleRoutes, points: vehiclePoints } =
        useVehicleRouteMapData(
            vehicleRoute,
            isVehicleShiftTabView
                ? selectedVehicleShift?.workShiftTimeSlots
                : undefined,
            isVehicleShiftTabView ? selectedWorkShiftTimeSlotId : undefined,
            isVehicleShiftTabView ? hoveredWorkShiftTimeSlotId : undefined
        );

    const { zoomKey, zoom, workShiftPositionsLoading } = useMapZoom({
        workShiftId: workShift?.workShiftId,
        selectedWorkShiftTimeSlotId,
        selectedVehicleId,
        isVehicleDetailView,
        isWorkShiftView,
        isVehicleRouteView,
        isVehicleShiftTabView,
        vehicleRoute,
        workshiftTimeSlotRoutes,
        workshiftPoints,
        selectedWorkTimeTypeIds,
        showNoWorkTime,
        dataLength: data?.length ?? 0,
        mapDataLoading,
        vehicleRouteLoading,
        reloadKey,
        reloadWorkShiftData: reload,
    });

    useEffect(() => {
        if (showOnMap || editMode) {
            void dispatch(fetchGeofences());
        }
    }, [showOnMap, editMode, dispatch]);

    const mapPoints = useMemo(
        () =>
            data
                ?.filter(
                    (d) =>
                        !selectedVehicleId || selectedVehicleId === d.vehicleId
                )
                .map(
                    (d) =>
                        ({
                            id: d.vehicleId,
                            coords: d.coords,
                            icon: (
                                <LocalShippingIcon
                                    className={`text-${getWorkTimeTypeContrastColorClass(
                                        d.workTimeTypeType
                                    )}`}
                                />
                            ),
                            text: d.registerNumber,
                            fullText: [
                                d.registerNumber,
                                d.employeeName,
                                d.workTimeTypeName,
                                d.driverGmt
                                    ? `${t("monitorView.tachograph.driverCardInserted")} ${Base.dateStrToOriginalTimezoneTimeStr(d.driverGmt)}`
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(" - "),
                            textColorClass: getWorkTimeTypeContrastColorClass(
                                d.workTimeTypeType
                            ),
                            textBgColorClass: getWorkTimeTypeColorClass(
                                d.workTimeTypeType
                            ),
                            groupColor: getWorkTimeTypeColorCode(
                                d.workTimeTypeType
                            ),
                            groupName: d.workTimeTypeName,
                        }) as MapPointProps
                ),
        [data, selectedVehicleId, t]
    );

    const handlePointSelect = useCallback(
        (id: string) => {
            if (isWorkShiftView || isVehicleShiftTabView) {
                dispatch(setSelectedWorkShiftTimeSlotId(id));
            } else {
                handleVehicleSelect(
                    id,
                    data,
                    isVehicleDetailView,
                    dispatch
                );
            }
        },
        [isWorkShiftView, isVehicleShiftTabView, data, isVehicleDetailView, dispatch]
    );

    const handleRouteSelect = useCallback(
        (id: string) => {
            if (isWorkShiftView || isVehicleShiftTabView) {
                dispatch(setSelectedWorkShiftTimeSlotId(id));
            }
        },
        [isWorkShiftView, isVehicleShiftTabView, dispatch]
    );

    const handlePointHover = useCallback(
        (id: string | null) => {
            if (isWorkShiftView || isVehicleShiftTabView) {
                dispatch(setHoveredWorkShiftTimeSlotId(id));
            }
        },
        [isWorkShiftView, isVehicleShiftTabView, dispatch]
    );

    const handleRouteHover = useCallback(
        (id: string | null) => {
            if (isWorkShiftView || isVehicleShiftTabView) {
                dispatch(setHoveredWorkShiftTimeSlotId(id));
            }
        },
        [isWorkShiftView, isVehicleShiftTabView, dispatch]
    );

    return (
        <Box
            display="flex"
            flexDirection="column"
            minHeight="100%"
            sx={{ position: "relative" }}
        >
            {workShiftPositionsLoading && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <LoadingIndicator loading />
                </Box>
            )}
            <div>
                <MapToolbar
                    zoom={zoom}
                    onRefresh={onRefresh}
                    loading={loading}
                    groupPoints={groupPoints}
                />
            </div>
            {!!mapPoints ? (
                <Map
                    points={
                        isVehicleShiftTabView || isVehicleRouteView
                            ? vehiclePoints
                            : !!workShift
                                ? workshiftPoints
                                : mapPoints
                    }
                    routes={
                        isVehicleShiftTabView || isVehicleRouteView
                            ? vehicleRoutes
                            : !!workShift
                                ? workshiftTimeSlotRoutes
                                : []
                    }
                    autoZoom={false}
                    zoomKey={zoomKey}
                    groupPoints={groupPoints}
                    pointSelectionEnabled
                    onPointSelect={handlePointSelect}
                    onRouteSelect={handleRouteSelect}
                    onPointHover={handlePointHover}
                    onRouteHover={handleRouteHover}
                    hoverMenuEnabled
                >
                    <GeofenceOverlay />
                    {drawingMode && <GeofenceDrawing />}
                    <MapControl position={ControlPosition.RIGHT_TOP}>
                        <GeofenceLegend />
                    </MapControl>
                </Map>
            ) : null}
        </Box>
    );
});

MonitorViewMap.displayName = "MonitorViewMap";
